import {Icon} from '@benchling/nucleus-ui';
import {
  Alert,
  Anchor,
  Box,
  Button,
  createStyles,
  Divider,
  Flex,
  Group,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import {warnAndRedirectAfterInaccessiblePageOpen} from 'auth/capabilities/InaccessibleFeaturesActions';
import {navigate, pluralize, redirectForDownload} from 'BenchlingUtil';
import ValidationErrorsTable, {SpreadsheetType} from 'bulk-import/panes/ValidationErrorsTable';
import {
  EMPTY_SPREADSHEET_VALIDATION_ERROR_INFO,
  ErrorData,
  RowErrorCode,
  SpreadsheetValidationError,
} from 'bulk-import/SpreadsheetBulkImportConstants';
import {SIDE_PADDING} from 'chemistry/ChemicalCounterpartConstants';
import {ChemicalCounterpartCSVValidationOutput} from 'chemistry/ChemistryDAO';
import makeCancelable from 'lib/makeCancelable';
import navigateToURL from 'lib/navigateToURL';
import notifications from 'notifications';
import EntityStore from 'stores/EntityStore';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Breadcrumb from 'ui-library/Breadcrumb';
import FileChooser from 'ui-library/FileChooser';
import FileList from 'ui-library/FileList';
import FileRow from 'ui-library/FileRow';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as ImportUtil from 'util/ImportUtil';
import {capitalize} from 'util/NameUtil';
import {withLoadingScreen} from 'util/Promises';

interface ValidationResult {
  importUrl: string;
  csvValidationOutput: ChemicalCounterpartCSVValidationOutput;
  errorData: ErrorData;
}

function errorsForRowValidation(
  output: ChemicalCounterpartCSVValidationOutput['rowValidationOutputs']
): ErrorData {
  if (output == null) {
    return EMPTY_SPREADSHEET_VALIDATION_ERROR_INFO;
  }
  const otherErrors: Array<SpreadsheetValidationError> = [];
  for (const [row, rowOutput] of output.entries()) {
    // errorMessages is empty for valid rows, so this naturally skips them.
    for (const errorMessage of rowOutput.errorMessages) {
      otherErrors.push({row, errorCode: RowErrorCode.OTHER_ERROR, errorMessage});
    }
  }
  return {...EMPTY_SPREADSHEET_VALIDATION_ERROR_INFO, otherErrors};
}

async function performAsyncWork(
  selectedFile: File,
  validate: (filename: string, url: string) => Promise<ChemicalCounterpartCSVValidationOutput>
): Promise<ValidationResult> {
  const url = await ImportUtil.upload(selectedFile);
  if (!url) {
    throw new Error('Upload did not return a URL');
  }
  const output = await validate(selectedFile.name, url);
  return {
    importUrl: url,
    csvValidationOutput: output,
    errorData: errorsForRowValidation(output.rowValidationOutputs),
  };
}

export interface ChemicalCounterpartCreateFromCSVPageProps {
  /** Singular lowercase name of the imported type, e.g. "salt". */
  counterpartName: string;
  libraryUrl: string;
  templateCSVLink: string;
  validate: (filename: string, url: string) => Promise<ChemicalCounterpartCSVValidationOutput>;
  createFromFile: (url: string, filename: string) => Promise<{createdIds: Array<string>}>;
  onImportSuccess?: () => Promise<void>;
  dataTestComponent: string;
}

export default function ChemicalCounterpartCreateFromCSVPage({
  counterpartName,
  libraryUrl,
  templateCSVLink,
  validate,
  createFromFile,
  onImportSuccess,
  dataTestComponent,
}: ChemicalCounterpartCreateFromCSVPageProps): JSX.Element | null {
  const {classes} = useStyles();
  const isTenantAdmin = useStoreSelector(
    EntityStore,
    useCallback((store: typeof EntityStore) => store.currentUser()?.isTenantAdmin ?? false, [])
  );
  const [file, setFile] = useState<File | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const cancelRef = useRef<(() => void) | undefined>();

  const counterpartNamePlural = pluralize(2, counterpartName);
  const isValidationLoading = file != null && validationResult == null;
  const csvValidationOutput = validationResult?.csvValidationOutput ?? null;
  const errorData = validationResult?.errorData ?? EMPTY_SPREADSHEET_VALIDATION_ERROR_INFO;
  const importUrl = validationResult?.importUrl ?? null;

  useEffect(() => {
    if (!isTenantAdmin) {
      warnAndRedirectAfterInaccessiblePageOpen(
        `You must be a tenant admin to import ${counterpartNamePlural}. Please contact your tenant admin for more information.`
      );
    }
  }, [isTenantAdmin, counterpartNamePlural]);

  const canSubmit =
    file != null &&
    !isValidationLoading &&
    csvValidationOutput != null &&
    csvValidationOutput.fileValidationError == null &&
    (csvValidationOutput.rowValidationOutputs?.every((output) => output.isValid) ?? false);

  const replaceSelectedFile = (newFile: File | null): void => {
    cancelRef.current?.();
    setFile(newFile);
    setValidationResult(null);
  };

  const uploadAndValidateCSV = async (selectedFile: File): Promise<void> => {
    replaceSelectedFile(selectedFile);

    const {promise, cancel} = makeCancelable(performAsyncWork(selectedFile, validate));
    cancelRef.current = cancel;
    try {
      const result = await promise;
      setValidationResult(result);
    } catch (e) {
      if ((e as {isCanceled?: boolean}).isCanceled) {
        return;
      }
      replaceSelectedFile(null);
      throw e;
    }
  };

  const clearSelectedFile = (): void => {
    replaceSelectedFile(null);
  };

  const createCounterparts = async (): Promise<void> => {
    if (importUrl == null || file == null) {
      return;
    }
    const {createdIds} = await createFromFile(importUrl, file.name);
    notifications.success(`Imported ${createdIds.length} ${pluralize(createdIds.length, counterpartName)}`);
    await onImportSuccess?.();
    navigateToURL(libraryUrl);
  };

  if (!isTenantAdmin) {
    return null;
  }

  return (
    <Stack
      h="100%"
      spacing={0}
      className={classes.page}
      {...getTestProps({component: dataTestComponent, element: 'root'})}
    >
      <Box className={classes.header}>
        <Stack spacing="xxs">
          <Breadcrumb
            items={[{label: `${capitalize(counterpartName)} library`, href: libraryUrl}]}
            isLastItemInteractable={true}
          />
          <Title order={1} size="h4" c="black">
            {`Add ${counterpartNamePlural}`}
          </Title>
        </Stack>
      </Box>
      <Stack spacing="3xl" className={classes.body}>
        <Stack spacing="lg" className={classes.uploadSection}>
          <Title order={2} size="h4" c="gray.9">
            Upload CSV
          </Title>
          <Alert variant="info" icon={<Icon icon="info-circle" />}>
            <Stack spacing="xs">
              <Flex justify="space-between" align="flex-start" gap="md">
                {/* Keep in sync with _REQUIRED_COLUMNS/_ALLOWED_COLUMNS in
                    benchling/chemistry/salts_solvents/csv_util.py */}
                <Text size="md" fw={FONT_WEIGHT_PROXIMA_SEMIBOLD} c="gray.9">
                  The CSV file must define &quot;name&quot; and &quot;smiles&quot; columns. It can also
                  include an optional &quot;code&quot; column.
                </Text>
                <Anchor
                  size="md"
                  fw={FONT_WEIGHT_PROXIMA_SEMIBOLD}
                  href={templateCSVLink}
                  className={classes.templateLink}
                  onClick={(e) => {
                    e.preventDefault();
                    redirectForDownload(templateCSVLink);
                  }}
                >
                  Download CSV template
                </Anchor>
              </Flex>
              <Text size="sm" c="gray.9">
                {`Each row creates one ${counterpartName}. Codes must be unique across all salts and solvents.`}
              </Text>
            </Stack>
          </Alert>
          <Box className={classes.dropzone}>
            {file == null ? (
              <FileChooser onSelectFiles={uploadAndValidateCSV} multiple={false} />
            ) : (
              <FileList>
                <FileRow file={file} onRequestRemove={clearSelectedFile} />
              </FileList>
            )}
          </Box>
        </Stack>
        <Divider />
        <Stack spacing="md">
          <Stack spacing="xs">
            <Title order={2} size="h4" c="gray.9">
              Review upload
            </Title>
            <Text size="md" c="gray.9">
              After you upload your spreadsheet, any errors with spreadsheet interpretation will appear here.
              Errors must be resolved before you can complete your import.
            </Text>
          </Stack>
          {file == null ? null : isValidationLoading ? (
            <CenteredHorizontalThrobber />
          ) : csvValidationOutput?.fileValidationError != null ? (
            <Alert variant="error" icon={<Icon icon="exclamation-circle" />}>
              {csvValidationOutput.fileValidationError}
            </Alert>
          ) : (
            <ValidationErrorsTable
              spreadsheetType={SpreadsheetType.SPREADSHEET}
              resolvableErrorStatuses={{}}
              errorData={errorData}
            />
          )}
        </Stack>
      </Stack>
      <Group position="apart" className={classes.footer}>
        <Button variant="secondary" component="a" href={libraryUrl} onClick={navigate}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={!canSubmit}
          onClick={() => withLoadingScreen(createCounterparts(), 'Importing...')}
          {...getTestProps({component: dataTestComponent, element: 'createButton'})}
        >
          {`Import ${counterpartNamePlural}`}
        </Button>
      </Group>
    </Stack>
  );
}

const useStyles = createStyles((theme) => ({
  page: {
    overflowY: 'auto',
  },
  header: {
    padding: `${SIDE_PADDING}px ${theme.spacing.lg} ${theme.spacing.lg} ${SIDE_PADDING}px`,
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
  },
  body: {
    flex: 1,
    paddingInline: SIDE_PADDING,
    paddingTop: theme.spacing.lg,
  },
  uploadSection: {
    maxWidth: 900,
  },
  templateLink: {
    whiteSpace: 'nowrap',
    flex: '0 0 auto',
  },
  dropzone: {
    // Max (not fixed) width so the dropzone shrinks with the content area on narrow viewports/zoom.
    maxWidth: 500,
    minHeight: 128,
  },
  footer: {
    padding: theme.spacing.lg,
    borderTop: `1px solid ${theme.colors.gray[3]}`,
  },
}));
